﻿import { FunctionComponent } from "react";
import { Box, Divider, Grid, GridItem, Image, Link, LinkProps, List, ListItem, Text, TextProps, VStack } from "@chakra-ui/react";
import { AMLogoWhite } from "../../components/Logo";

/**
 * Use this footer on all A&M branded projects.
 */
export const Footer: FunctionComponent<{ assetsPath?: string }> = ({ assetsPath = "/assets/footer" }) => {
    return (
        <Box as="footer" color={"whiteAlpha.900"} bgColor={"blue.900"} py={50} px={10}>
            <VStack as="footer" gap={"1rem"} align={"start"} maxWidth={"1700px"} margin={"auto"}>
                <Grid templateColumns={{ base: "repeat(3, 1fr)", md: "repeat(4, auto)", lg: "repeat(5, auto)" }} gap={"14px"} rowGap={"30px"} w={"100%"}>
                    <GridItem colSpan={{ base: 3, md: 1 }}>
                        <Image as={AMLogoWhite} boxSize="125px" aria-label="Alvarez and Marsal Tax Company Logo" />
                    </GridItem>
                    <List>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/global-locations"}>Global Locations</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/expertise"}>Expertise</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/industries"}>Industries</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/insights"}>Insights</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/our-people"}>Our People</FooterLink>
                        </ListItem>
                    </List>
                    <List>
                        <ListItem>
                            <FooterLink href={"https://careers.alvarezandmarsal.com/"}>Careers</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/about-am"}>About A&amp;M</FooterLink>
                        </ListItem>
                    </List>
                    <List>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/#block-connect-with-us"}>Contact Us</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.alvarezandmarsal.com/alumni"}>Alumni</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://a-mcapital.com/"}>A&amp;M Capital</FooterLink>
                        </ListItem>
                        <ListItem>
                            <FooterLink href={"https://www.amcapitalre.com/"}>A&amp;M Real Estate</FooterLink>
                        </ListItem>
                    </List>
                    <GridItem as={List} colSpan={{ base: 3, md: 4, lg: 1 }}>
                        <ListItem>
                            <FooterLinkLight href={"https://www.alvarezandmarsal.com/privacy-notice"}>Privacy Notice</FooterLinkLight>
                        </ListItem>
                        <ListItem>
                            <FooterLinkLight href={"https://www.alvarezandmarsal.com/DPF-notice-alvarez-marsal-disputes-and-investigations-llc"}>
                                EU-US/Swiss-US Data Protection Framework Notice
                            </FooterLinkLight>
                        </ListItem>
                        <ListItem>
                            <FooterLinkLight href={"https://www.alvarezandmarsal.com/privacy-notice-california"}>California Privacy Notice</FooterLinkLight>
                        </ListItem>
                        <ListItem>
                            <FooterLinkLight href={"https://www.alvarezandmarsal.com/cookie-policy"}>Cookie Notice</FooterLinkLight>
                        </ListItem>
                        <ListItem>
                            <FooterLinkLight href={"https://www.alvarezandmarsal.com/terms-use"}>Terms of Use</FooterLinkLight>
                        </ListItem>
                    </GridItem>
                </Grid>
                <Divider borderColor={"white"} />
                <Box>
                    <FinePrint>© Copyright 2023, Alvarez & Marsal Holdings, LLC. All Rights Reserved.</FinePrint>
                    <FinePrint pb={"9px"}>
                        ALVAREZ & MARSAL®,&nbsp;
                        <Image alt="A&amp;M Logo" src={`${assetsPath}/am_logo_white.png`} width="20px" height="18px" loading="lazy" display="inline-block" />
                        ®,&nbsp;
                        <Image
                            alt="A&amp;M Logo"
                            src={`${assetsPath}/am_logo_white-old.png`}
                            width="20px"
                            height="18px"
                            loading="lazy"
                            display="inline-block"
                        />
                        ®,&nbsp;
                        <Image
                            alt="A&amp;M Logo"
                            src={`${assetsPath}/am_logo_white_global-old.png`}
                            width="61px"
                            height="35px"
                            loading="lazy"
                            display="inline-block"
                            mb={"-9px"}
                        />
                        ® and A&M® are trademarks of Alvarez & Marsal Holdings, LLC.
                    </FinePrint>
                    <FinePrint>Note: Alvarez & Marsal employs CPAs, but is not a licensed CPA firm.</FinePrint>
                </Box>
            </VStack>
        </Box>
    );
};

const FooterLinkLight: FunctionComponent<LinkProps> = ({ children, ...linkProps }) => {
    return (
        <FooterLink fontWeight={300} color={"whiteAlpha.800"} {...linkProps}>
            {children}
        </FooterLink>
    );
};

const FooterLink: FunctionComponent<LinkProps> = ({ children, ...linkProps }) => {
    return (
        <Link fontWeight={500} fontSize={"14px"} _hover={{ textDecoration: "none" }} {...linkProps}>
            {children}
        </Link>
    );
};

const FinePrint: FunctionComponent<TextProps> = ({ children, ...textProps }) => {
    return (
        <Text color={"white"} fontFamily={"Helvetica Neue Extra"} fontWeight={100} fontSize={"9px"} my={"9px"} {...textProps}>
            {children}
        </Text>
    );
};
